import { useEffect, useState } from 'react';
import mqtt from 'mqtt';
import { apiUrl, MQTT_DEFAULT_TOPIC, MQTT_WS_URL } from '../../shared/api/apiConfig';
import { formatDisplayDateTime } from '../../shared/utils/dateFormat';
import { useMqttHub } from './useMqttHub';
import './MqttMessagesPage.css';

const DEFAULT_PAYLOAD = JSON.stringify(
  {
    "messageId": "point-001",
    "deviceId": "test",
    "vehicleRegistration": "ABC-123",
    "recordedAt": new Date().toISOString(),
    "latitude": -33.8688,
    "longitude": 151.2093
  },
  null,
  2,
);

const STATUS_LABELS = {
  connecting: 'Connecting',
  connected: 'Connected',
  reconnecting: 'Reconnecting',
  disconnected: 'Disconnected',
};

const ACK_TIMEOUT_MS = 5_000;
const METRICS_REFRESH_MS = 5_000;

let optimisticMessageSequence = 0;

function getPageProtocol() {
  if (typeof window === 'undefined') {
    return 'http:';
  }

  return window.location.protocol;
}



export function getBrowserWsValidationError(brokerUrl, pageProtocol = getPageProtocol()) {
  const trimmedBrokerUrl = brokerUrl.trim();

  if (!trimmedBrokerUrl) {
    return 'Enter an MQTT WebSocket URL such as ws://localhost:9001.';
  }

  if (pageProtocol === 'https:' && trimmedBrokerUrl.startsWith('ws://')) {
    return 'This page is served over HTTPS. Use a secure MQTT WebSocket URL (wss://).';
  }

  return '';
}

export function formatPublishError(error) {
  const fallbackMessage = 'Failed to publish MQTT message from the browser.';

  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : fallbackMessage;

  const normalizedErrorMessage = errorMessage.toLowerCase();

  if (normalizedErrorMessage.includes('not authorized') || normalizedErrorMessage.includes('unable to connect to the mqtt broker')) {
    return 'The MQTT broker rejected the connection. Check the broker host, port, username, password, and topic permissions.';
  }

  if (normalizedErrorMessage.includes('mixed content') || normalizedErrorMessage.includes('secure mqtt websocket url')) {
    return 'Browser MQTT publish needs a secure WebSocket endpoint (`wss://`) when this site is loaded over HTTPS.';
  }

  if (normalizedErrorMessage.includes('connack timeout') || normalizedErrorMessage.includes('timed out')) {
    return 'The browser could not complete the MQTT WebSocket connection. Check that the broker exposes MQTT over WebSockets at this URL.';
  }

  if (
    normalizedErrorMessage.includes('failed to fetch') ||
    normalizedErrorMessage.includes('networkerror') ||
    normalizedErrorMessage.includes('load resource') ||
    normalizedErrorMessage.includes('enotfound') ||
    normalizedErrorMessage.includes('econnrefused')
  ) {
    return 'The MQTT WebSocket endpoint could not be reached. Check the broker WebSocket URL and networking.';
  }

  if (normalizedErrorMessage.includes('websocket') || normalizedErrorMessage.includes('ws://') || normalizedErrorMessage.includes('wss://')) {
    return 'The MQTT broker WebSocket connection failed. Verify that the broker exposes MQTT over WebSockets and that the URL is correct.';
  }

  return errorMessage || fallbackMessage;
}

function createOptimisticMessageId(topic) {
  optimisticMessageSequence += 1;
  return `local-${topic}-${Date.now()}-${optimisticMessageSequence}`;
}

function normalizeMessage(message) {
  if (!message) {
    return null;
  }

  const topic = message.topic ?? '';
  const payload = message.payload ?? '';
  const receivedAtUtc = message.receivedAtUtc ?? new Date().toISOString();

  return {
    id: message.id ?? `${topic}-${receivedAtUtc}-${payload}`,
    topic,
    payload,
    receivedAtUtc,
    responseTopic: message.responseTopic ?? '',
    correlationData: message.correlationData ?? '',
    acknowledgementTopic: message.acknowledgementTopic ?? '',
    acknowledgementStatus: message.acknowledgementStatus ?? 'pending',
    acknowledgedAtUtc: message.acknowledgedAtUtc ?? '',
    acknowledgementFailureReason: message.acknowledgementFailureReason ?? '',
    acknowledgementPayload: message.acknowledgementPayload ?? '',
    isOptimistic: false,
  };
}

function looksLikeJson(value) {
  if (!value) {
    return false;
  }

  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

function parseJsonPayload(value) {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function extractAcknowledgementMessage(message) {
  const payload = parseJsonPayload(message.payload);

  if (!payload || payload.status !== 'received' || !payload.originalTopic) {
    return null;
  }

  return {
    messageId: payload.messageId ?? '',
    originalTopic: payload.originalTopic,
    topic: message.topic,
    payload: message.payload,
    receivedAtUtc: message.receivedAtUtc,
    correlationData: message.correlationData,
  };
}

function buildDisplayMessages(messages) {
  const originalMessages = [];
  const acknowledgements = [];

  for (const message of messages) {
    const acknowledgement = extractAcknowledgementMessage(message);

    if (acknowledgement) {
      acknowledgements.push(acknowledgement);
    } else {
      originalMessages.push({ ...message });
    }
  }

  for (const acknowledgement of acknowledgements) {
    const matchedMessage = originalMessages.find((message) => {
      if (acknowledgement.messageId && message.id === acknowledgement.messageId) {
        return true;
      }

      return message.topic === acknowledgement.originalTopic && !message.acknowledgementPayload;
    });

    if (!matchedMessage) {
      continue;
    }

    matchedMessage.acknowledgementTopic = acknowledgement.topic;
    matchedMessage.acknowledgementStatus = 'received';
    matchedMessage.acknowledgedAtUtc = acknowledgement.receivedAtUtc;
    matchedMessage.acknowledgementPayload = acknowledgement.payload;
    matchedMessage.correlationData = acknowledgement.correlationData || matchedMessage.correlationData;
    matchedMessage.acknowledgementFailureReason = '';
  }

  return originalMessages;
}

function formatTimestamp(value) {
  return formatDisplayDateTime(value, 'Unknown');
}

function formatCount(value) {
  return Number.isFinite(value) ? value.toLocaleString() : '—';
}

function formatAgeSeconds(value) {
  if (value === null) {
    return 'No backlog';
  }

  if (!Number.isFinite(value)) {
    return '—';
  }

  if (value < 1) {
    return '<1 s';
  }

  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} s`;
}

function formatDurationMilliseconds(value) {
  if (value === null) {
    return 'No batches yet';
  }

  if (!Number.isFinite(value)) {
    return '—';
  }

  if (value < 1_000) {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ms`;
  }

  return `${(value / 1_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} s`;
}

function mergeMessage(currentMessages, nextMessage) {
  return [
    nextMessage,
    ...currentMessages.filter((item) => item.id !== nextMessage.id),
  ].slice(0, 50);
}

function addOptimisticMessage(currentMessages, nextMessage) {
  return [
    nextMessage,
    ...currentMessages,
  ].slice(0, 50);
}

function removeMessageById(currentMessages, messageId) {
  return currentMessages.filter((item) => item.id !== messageId);
}

function updateMessageAcknowledgement(currentMessages, messageId, acknowledgement) {
  return currentMessages.map((item) => {
    if (item.id !== messageId) {
      return item;
    }

    return {
      ...item,
      ...acknowledgement,
    };
  });
}

function replaceMatchingOptimisticMessage(currentMessages, nextMessage) {
  const optimisticMessageIndex = currentMessages.findIndex(
    (item) => item.isOptimistic && item.topic === nextMessage.topic && item.payload === nextMessage.payload,
  );

  if (optimisticMessageIndex === -1) {
    return mergeMessage(currentMessages, nextMessage);
  }

  const updatedMessages = [...currentMessages];
  updatedMessages[optimisticMessageIndex] = nextMessage;
  return updatedMessages.slice(0, 50);
}

function decodeMqttBytes(value) {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof Buffer !== 'undefined' && Buffer.isBuffer?.(value)) {
    return value.toString('utf8');
  }

  return new TextDecoder().decode(value);
}

function waitForMqttAcknowledgement(client, ackTopic, expectedCorrelationData, timeoutMs = ACK_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out waiting for MQTT ACK on ${ackTopic}.`));
    }, timeoutMs);

    function cleanup() {
      window.clearTimeout(timeoutId);

      if (typeof client.off === 'function') {
        client.off('message', handleMessage);
      } else if (typeof client.removeListener === 'function') {
        client.removeListener('message', handleMessage);
      }
    }

    function handleMessage(receivedTopic, receivedPayload, packet = {}) {
      if (receivedTopic !== ackTopic) {
        return;
      }

      const receivedCorrelationData = decodeMqttBytes(packet.properties?.correlationData);

      if (expectedCorrelationData && receivedCorrelationData && receivedCorrelationData !== expectedCorrelationData) {
        return;
      }

      cleanup();
      resolve({
        topic: receivedTopic,
        payload: decodeMqttBytes(receivedPayload),
        correlationData: receivedCorrelationData,
        acknowledgedAtUtc: new Date().toISOString(),
      });
    }

    client.on('message', handleMessage);
  });
}

export default function MqttMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [brokerUrl, setBrokerUrl] = useState(MQTT_WS_URL);
  const [topic, setTopic] = useState(MQTT_DEFAULT_TOPIC);
  const [responseTopic, setResponseTopic] = useState('');
  const [correlationData, setCorrelationData] = useState('');
  const [payload, setPayload] = useState(DEFAULT_PAYLOAD);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clientId, setClientId] = useState(
    () => `gps-test`,
  );
  const [publishState, setPublishState] = useState({ type: 'idle', message: '' });
  const [clearState, setClearState] = useState({ type: 'idle', message: '' });
  const [ingestionMetrics, setIngestionMetrics] = useState(null);
  const [metricsState, setMetricsState] = useState({ type: 'loading', message: 'Loading ingestion metrics...' });

  useEffect(() => {
    const abortController = new AbortController();

    async function loadIngestionMetrics() {
      try {
        const response = await fetch(apiUrl('/gps/ingestion/metrics'), {
          signal: abortController.signal,
        });
        const responseBody = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(responseBody.message ?? 'GPS ingestion metrics are unavailable.');
        }

        setIngestionMetrics(responseBody);
        setMetricsState({ type: 'success', message: '' });
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        setMetricsState({
          type: 'error',
          message: 'GPS ingestion metrics are unavailable. Live MQTT messages will continue to update.',
        });
      }
    }

    loadIngestionMetrics();
    const refreshId = window.setInterval(loadIngestionMetrics, METRICS_REFRESH_MS);

    return () => {
      abortController.abort();
      window.clearInterval(refreshId);
    };
  }, []);

  useMqttHub({
    onHistoryLoaded: (history) => {
      setMessages((history ?? []).map(normalizeMessage).filter(Boolean));
    },
    onMessageReceived: (message) => {
      const normalizedMessage = normalizeMessage(message);

      if (!normalizedMessage) {
        return;
      }

      setMessages((currentMessages) => replaceMatchingOptimisticMessage(currentMessages, normalizedMessage));
    },
    onConnectionStateChange: setConnectionStatus,
  });

  async function handlePublish(event) {
    event.preventDefault();
    const publishTopic = topic.trim() || MQTT_DEFAULT_TOPIC;
    const trimmedResponseTopic = responseTopic.trim();
    const trimmedCorrelationData = correlationData.trim();
    const acknowledgementRequested = Boolean(trimmedResponseTopic);

    if (!publishTopic) {
      setPublishState({
        type: 'error',
        message: 'Enter an MQTT topic before publishing.',
      });
      return;
    }

    let optimisticMessage = null;

    try {
      const publishBrokerUrl = brokerUrl.trim();
      const browserWsValidationError = getBrowserWsValidationError(publishBrokerUrl);

      if (browserWsValidationError) {
        setPublishState({
          type: 'error',
          message: browserWsValidationError,
        });
        return;
      }

      optimisticMessage = {
        id: createOptimisticMessageId(publishTopic),
        topic: publishTopic,
        payload,
        receivedAtUtc: new Date().toISOString(),
        responseTopic: trimmedResponseTopic,
        correlationData: acknowledgementRequested ? trimmedCorrelationData : '',
        acknowledgementStatus: acknowledgementRequested ? 'pending' : 'skipped',
        isOptimistic: true,
      };

      setMessages((currentMessages) => addOptimisticMessage(currentMessages, optimisticMessage));
      setPublishState({ type: 'pending', message: 'Publishing message from the browser...' });

      const client = await mqtt.connectAsync(publishBrokerUrl, {
        clean: true,
        clientId: clientId.trim(),
        protocolVersion: 5,
        connectTimeout: 10_000,
        reconnectPeriod: 0,
        username: username.trim() || undefined,
        password: password || undefined,
      });

      try {
        const publishOptions = {
          qos: 2,
        };

        if (acknowledgementRequested) {
          publishOptions.properties = {
            responseTopic: trimmedResponseTopic,
          };

          if (trimmedCorrelationData) {
            publishOptions.properties.correlationData = new TextEncoder().encode(trimmedCorrelationData);
          }

          await client.subscribeAsync(trimmedResponseTopic, { qos: 2 });
          const acknowledgementPromise = waitForMqttAcknowledgement(
            client,
            trimmedResponseTopic,
            trimmedCorrelationData,
          );
          await client.publishAsync(publishTopic, payload, publishOptions);
          const acknowledgement = await acknowledgementPromise;

          setMessages((currentMessages) =>
            updateMessageAcknowledgement(currentMessages, optimisticMessage.id, {
              acknowledgementTopic: acknowledgement.topic,
              acknowledgementStatus: 'received',
              acknowledgedAtUtc: acknowledgement.acknowledgedAtUtc,
              acknowledgementPayload: acknowledgement.payload,
              correlationData: acknowledgement.correlationData || trimmedCorrelationData,
              acknowledgementFailureReason: '',
            }),
          );
        } else {
          await client.publishAsync(publishTopic, payload, publishOptions);
        }
      } finally {
        await client.endAsync().catch(() => {});
      }

      setPublishState({
        type: 'success',
        message: acknowledgementRequested
          ? `Published to ${publishTopic} and received ACK on ${trimmedResponseTopic}.`
          : `Published to ${publishTopic} without requesting an ACK.`,
      });
    } catch (error) {
      if (optimisticMessage) {
        const isAcknowledgementTimeout =
          error instanceof Error && error.message.includes('Timed out waiting for MQTT ACK');

        if (isAcknowledgementTimeout) {
          setMessages((currentMessages) =>
            updateMessageAcknowledgement(currentMessages, optimisticMessage.id, {
              acknowledgementStatus: 'failed',
              acknowledgementTopic: trimmedResponseTopic,
              acknowledgementFailureReason: error.message,
            }),
          );
        } else {
          setMessages((currentMessages) => removeMessageById(currentMessages, optimisticMessage.id));
        }
      }

      setPublishState({
        type: 'error',
        message: formatPublishError(error),
      });
    }
  }

  async function handleClearMessages() {
    setClearState({ type: 'pending', message: 'Clearing stored messages...' });

    try {
      const response = await fetch(apiUrl('/mqtt/messages'), {
        method: 'DELETE',
      });

      const responseBody = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseBody.message ?? 'Failed to clear stored MQTT messages.');
      }

      setMessages([]);
      setClearState({
        type: 'success',
        message: 'Stored MQTT messages cleared.',
      });
    } catch (error) {
      setClearState({
        type: 'error',
        message: formatPublishError(error),
      });
    }
  }

  const statusLabel = STATUS_LABELS[connectionStatus] ?? 'Unknown';
  const displayMessages = buildDisplayMessages(messages);
  const messageCountLabel = `${displayMessages.length} message${displayMessages.length === 1 ? '' : 's'}`;

  return (
    <section className="mqtt-page">
      <div className="mqtt-hero">
        <div>
          <p className="mqtt-eyebrow">MQTT Monitor</p>
          <h1>Live messages from the new MQTT hub</h1>
          <p className="mqtt-subtitle">
            Publish directly from frontend JavaScript over MQTT WebSockets and watch the live feed update in real time.
          </p>
        </div>
        <div className={`mqtt-status mqtt-status-${connectionStatus}`}>
          <span className="mqtt-status-dot" />
          {statusLabel}
        </div>
      </div>

      <section className="mqtt-metrics" aria-labelledby="mqtt-metrics-title">
        <div className="mqtt-metrics-header">
          <div>
            <p className="mqtt-metrics-eyebrow">GPS ingestion</p>
            <h2 id="mqtt-metrics-title">Pipeline health</h2>
          </div>
          {ingestionMetrics?.observedAtUtc ? (
            <span className="mqtt-metrics-updated">
              Updated {formatTimestamp(ingestionMetrics.observedAtUtc)}
            </span>
          ) : null}
        </div>

        {metricsState.type === 'error' ? (
          <p className="mqtt-metrics-unavailable" role="status">{metricsState.message}</p>
        ) : null}

        {metricsState.type !== 'error' || ingestionMetrics ? (
          <div className="mqtt-metrics-grid" aria-busy={metricsState.type === 'loading'}>
            <div className="mqtt-metric">
              <span>Pending</span>
              <strong>{formatCount(ingestionMetrics?.queueDepth)}</strong>
              <small>Not completed by the database writer</small>
            </div>
            <div className="mqtt-metric">
              <span>Oldest pending</span>
              <strong>{formatAgeSeconds(ingestionMetrics?.oldestMessageAgeSeconds)}</strong>
              <small>Age since backend receipt</small>
            </div>
            <div className="mqtt-metric">
              <span>Last DB batch</span>
              <strong>{formatDurationMilliseconds(ingestionMetrics?.lastBatchDurationMilliseconds)}</strong>
              <small>{formatCount(ingestionMetrics?.lastBatchSize)} points attempted</small>
            </div>
            <div className="mqtt-metric">
              <span>MQTT redeliveries</span>
              <strong>{formatCount(ingestionMetrics?.redeliveriesTotal)}</strong>
              <small>Valid GPS messages with DUP set</small>
            </div>
            <div className="mqtt-metric">
              <span>Persisted</span>
              <strong>{formatCount(ingestionMetrics?.pointsPersistedTotal)}</strong>
              <small>Points stored since startup</small>
            </div>
            <div className="mqtt-metric">
              <span>Ignored</span>
              <strong>{formatCount(ingestionMetrics?.pointsIgnoredTotal)}</strong>
              <small>Duplicate or conflicting points</small>
            </div>
          </div>
        ) : null}
      </section>

      <div className="mqtt-grid">
        <form className="mqtt-card mqtt-publish-card" onSubmit={handlePublish}>
          <div className="mqtt-card-header">
            <div>
              <h2>Send from browser JavaScript</h2>
              <p>Publish directly from the browser using MQTT over WebSockets.</p>
            </div>
          </div>



          <label className="mqtt-field">
            <span>Broker WebSocket URL</span>
            <input
              value={brokerUrl}
              onChange={(event) => setBrokerUrl(event.target.value)}
              placeholder={getPageProtocol() === 'https:' ? 'wss://broker.example.com/mqtt' : 'ws://localhost:9001'}
            />
          </label>

          {getPageProtocol() === 'https:' ? (
            <div className="mqtt-mode-note">
              This page is served over HTTPS, so browser publish requires a secure `wss://` MQTT WebSocket endpoint.
            </div>
          ) : null}

          <label className="mqtt-field">
            <span>Topic</span>
            <input
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
              placeholder="test"
            />
          </label>

          <div className="mqtt-auth-grid">
            <label className="mqtt-field">
              <span>Response topic</span>
              <input
                value={responseTopic}
                onChange={(event) => setResponseTopic(event.target.value)}
                placeholder="Optional: device/test/gps/ack"
              />
            </label>

            <label className="mqtt-field">
              <span>Correlation data</span>
              <input
                value={correlationData}
                onChange={(event) => setCorrelationData(event.target.value)}
                placeholder="Optional request id"
              />
            </label>
          </div>

          <div className="mqtt-auth-grid">
            <label className="mqtt-field">
              <span>Username</span>
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Optional"
              />
            </label>

            <label className="mqtt-field">
              <span>Password</span>
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
                placeholder="Optional"
              />
            </label>
          </div>

          <label className="mqtt-field">
            <span>MQTT client ID</span>
            <input
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              placeholder="gps-test"
              maxLength={128}
              required
            />
          </label>

          <label className="mqtt-field">
            <span>Payload</span>
            <textarea
              value={payload}
              onChange={(event) => setPayload(event.target.value)}
              placeholder="Enter the MQTT payload"
            />
          </label>

          <div className="mqtt-publish-actions">
            <button type="submit" className="mqtt-primary-btn">
              Publish From Browser
            </button>
            {publishState.message ? (
              <p className={`mqtt-feedback mqtt-feedback-${publishState.type}`}>{publishState.message}</p>
            ) : null}
          </div>
        </form>

        <div className="mqtt-card mqtt-stream-card">
          <div className="mqtt-card-header">
            <div>
              <h2>Incoming stream</h2>
              <p>The newest messages appear at the top as soon as the hub receives them.</p>
            </div>
            <div className="mqtt-stream-actions">
              <span className="mqtt-count-pill">{messageCountLabel}</span>
              <button
                type="button"
                className="mqtt-secondary-btn"
                onClick={handleClearMessages}
                disabled={clearState.type === 'pending'}
              >
                Clear Messages
              </button>
            </div>
          </div>

          {clearState.message ? (
            <p className={`mqtt-feedback mqtt-feedback-${clearState.type}`}>{clearState.message}</p>
          ) : null}

          {displayMessages.length === 0 ? (
            <div className="mqtt-empty-state">
              <strong>No MQTT messages yet.</strong>
              <p>Publish one from the form or send one to the configured broker topic.</p>
            </div>
          ) : (
            <div className="mqtt-message-list">
              {displayMessages.map((message) => (
                <article key={message.id} className="mqtt-message-card">
                  <div className="mqtt-message-meta">
                    <div className="mqtt-topic-group">
                      <span className="mqtt-topic-chip">{message.topic || 'No topic'}</span>
                      <span className="mqtt-response-chip">
                        Response: {message.responseTopic || 'Not requested'}
                      </span>
                    </div>
                    <time dateTime={message.receivedAtUtc}>{formatTimestamp(message.receivedAtUtc)}</time>
                  </div>
                  <pre className={`mqtt-payload ${looksLikeJson(message.payload) ? 'is-json' : ''}`}>
                    {message.payload}
                  </pre>
                  <div className="mqtt-ack-panel">
                    <div className="mqtt-ack-header">
                      <span className={`mqtt-ack-badge mqtt-ack-${message.acknowledgementStatus || 'pending'}`}>
                        ACK {message.acknowledgementStatus || 'pending'}
                      </span>
                      {message.acknowledgedAtUtc ? (
                        <time dateTime={message.acknowledgedAtUtc}>
                          {formatTimestamp(message.acknowledgedAtUtc)}
                        </time>
                      ) : null}
                    </div>

                    <dl className="mqtt-ack-details">
                      <div>
                        <dt>ACK topic</dt>
                        <dd>{message.acknowledgementTopic || 'Not published yet'}</dd>
                      </div>
                      {message.correlationData ? (
                        <div>
                          <dt>Correlation</dt>
                          <dd>{message.correlationData}</dd>
                        </div>
                      ) : null}
                      {message.acknowledgementFailureReason ? (
                        <div>
                          <dt>ACK detail</dt>
                          <dd>{message.acknowledgementFailureReason}</dd>
                        </div>
                      ) : null}
                    </dl>
                    {message.acknowledgementPayload ? (
                      <pre className={`mqtt-ack-payload ${looksLikeJson(message.acknowledgementPayload) ? 'is-json' : ''}`}>
                        {message.acknowledgementPayload}
                      </pre>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
